import deleteForeverIcon from "@ms/delete_forever.svg?raw";
import { Checkbox } from "@ui/Checkbox";
import { ClipCount } from "@ui/molecules/ClipCount/ClipCount";
import { Search } from "@ui/Search";
import { HStack, Stack } from "@ui/Stack";
import { Toggle } from "@ui/Toggle";
import { Text } from "@ui/typography/Text";
import { type Accessor, createMemo, Show } from "solid-js";
import { Button } from "~/modules/shared/gvtui/Button";
import { Icon } from "~/modules/shared/gvtui/Icon";
import { useBoardDetails } from "../BoardDetailsContext";

export const ClipsMenu = () => {
	const boardDetails = useBoardDetails();

	const label = createMemo(() => {
		const count = boardDetails.selectedClipIds().length;
		return count > 0 ? `Remove (${count})` : "Remove";
	});

	const downloadLabel = createMemo(() => {
		const count = boardDetails.selectedClipIds().length;
		return count > 0 ? `Download (${count})` : "Download";
	});

	const disabled = createMemo(
		() => boardDetails.selectedClipIds().length === 0,
	);

	return (
		<HStack spacing="small" justify="space-between" class="clips-menu">
			<ClipCount clipCount={boardDetails.clipCount()} />
			<HStack spacing="small">
				<Search
					{...boardDetails.searchProps}
				/>
				<ToggleDetailsButton
					showDetails={boardDetails.showDetails}
					setShowDetails={boardDetails.setShowDetails}
				/>
				<Show when={boardDetails.canManage()}>
					<Stack
						direction="row-reverse"
						spacing="small"
						align="center"
						class="clips-menu__bulk-select-group"
					>
						<BulkSelectButton
							active={() => boardDetails.mode() === "bulk_select"}
							setActive={(active) =>
								boardDetails.setMode(active ? "bulk_select" : "view")
							}
						/>
						<Show when={boardDetails.mode() === "bulk_select"}>
							<HStack spacing="small" class="clips-menu__bulk-actions">
								<Button
									theme="black"
									disabled={disabled()}
									onClick={() =>
										boardDetails.deleteClips(boardDetails.selectedClipIds())
									}
								>
									<Icon
										icon={deleteForeverIcon}
										width="1.05rem"
										height="1.05rem"
									/>
									{label()}
								</Button>
								<Show when={boardDetails.showDownloadButton}>
									<Button
										disabled={disabled()}
										onClick={() =>
											boardDetails.downloadClips(boardDetails.selectedClipIds())
										}
										theme="purple"
										symbol="download"
										symbolRelativeSize="even-larger"
									>
										{downloadLabel()}
									</Button>
								</Show>
							</HStack>
						</Show>
					</Stack>
				</Show>
			</HStack>
		</HStack>
	);
};

const ToggleDetailsButton = (props: {
	showDetails: Accessor<boolean>;
	setShowDetails: (showDetails: boolean) => void;
}) => {
	return (
		<label class="board-details-toggle-label" for="details-toggle">
			<HStack align="center" spacing="small">
				<Text size="small" weight="bold" color="ink">
					Details
				</Text>
				<Toggle
					checked={props.showDetails}
					theme="ink"
					id="details-toggle"
					onChange={(event) =>
						props.setShowDetails(
							(event.target as HTMLInputElement).checked,
						)
					}
				/>
			</HStack>
		</label>
	);
};

const BulkSelectButton = (props: {
	active: Accessor<boolean>;
	setActive: (active: boolean) => void;
}) => {
	return (
		<label
			class="board-details-toggle-label clips-menu__delete-button"
			for="bulk-select-toggle"
			classList={{
				"clips-menu__delete-button--active": props.active(),
			}}
		>
			<Checkbox
				id="bulk-select-toggle"
				size="small"
				theme="ink"
				checked={props.active()}
				onChange={() => props.setActive(!props.active())}
			/>
			<span class="clips-menu__bulk-select-label">
				<Label label="Bulk select" active={props.active} />
				<Label label="Done" active={() => !props.active()} />
			</span>
		</label>
	);
};

const Label = (props: { label: string; active: Accessor<boolean> }) => {
	return (
		<span
			class="clips-menu__bulk-select-label-text"
			classList={{
				"clips-menu__bulk-select-label-text--visible": !props.active(),
			}}
			aria-hidden={props.active()}
		>
			<Text size="small" weight="bold">
				{props.label}
			</Text>
		</span>
	);
};
